import React from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Alert,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { Business, ExpandMore } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../hooks/useOrganization';
import { useAuthStore } from '../../auth/stores/authStore';

const OrganizationDisplay: React.FC = () => {
  const { data, isLoading: loading, error } = useOrganization();
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  const currentOrganization = data?.currentOrganisation || null;
  const availableOrganizations = data?.availableOrganisations || [];

  const handleOrganizationChange = (organizationId: string) => {
    const selectedOrg = availableOrganizations.find(
      org => org.id === organizationId
    );
    if (selectedOrg) {
      navigate(
        `/organization/switching/${selectedOrg.id}/${encodeURIComponent(selectedOrg.name)}`
      );
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <Box
        data-testid="organization-loading"
        sx={{
          px: 2,
          py: 1,
          borderBottom: 1,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Business sx={{ color: 'text.secondary', fontSize: 20 }} />

        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="80%" height={20} />
          <Skeleton variant="text" width="60%" height={16} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        data-testid="organization-error"
        sx={{
          px: 2,
          py: 1,
          borderTop: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Alert
          data-testid="organization-error-alert"
          severity="warning"
          sx={{
            fontSize: '0.75rem',
            '& .MuiAlert-message': {
              fontSize: '0.65rem',
              padding: '4px 0',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
            '& .MuiAlert-icon': {
              fontSize: '1rem',
            },
            '& .MuiAlert-action': {
              padding: '0 4px',
            },
            padding: '4px 8px',
          }}
        >
          {/* Use error.message to match E2E test expectations */}
          {error instanceof Error
            ? error.message
            : 'Failed to load organization'}
        </Alert>
      </Box>
    );
  }

  if (!currentOrganization) {
    // Show loading state when we don't have organization data but are authenticated
    return (
      <Box
        data-testid="organization-loading-no-data"
        sx={{
          px: 2,
          py: 1,
          borderBottom: 1,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Business sx={{ color: 'text.secondary', fontSize: 20 }} />

        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="80%" height={20} />
          <Skeleton variant="text" width="60%" height={16} />
        </Box>
      </Box>
    );
  }

  const hasAvailableOrganizations =
    availableOrganizations && availableOrganizations.length > 0;

  return (
    <Box
      data-testid="organization-display"
      sx={{
        px: 2,
        py: 1,
        borderTop: 1,
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Business sx={{ color: 'primary.main', fontSize: 20 }} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {hasAvailableOrganizations ? (
          <FormControl size="small" fullWidth variant="standard">
            <Select
              data-testid="organisation-selector"
              value={currentOrganization.id}
              onChange={e => handleOrganizationChange(e.target.value)}
              displayEmpty
              renderValue={() => currentOrganization.name}
              sx={{
                fontWeight: 'medium',
                color: 'text.primary',
                '& .MuiSelect-select': {
                  paddingTop: 0,
                  paddingBottom: 0,
                  fontSize: '0.875rem',
                },
                '&:before': { borderBottom: 'none' },
                '&:after': { borderBottom: 'none' },
                '&:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
              }}
              IconComponent={ExpandMore}
            >
              {availableOrganizations.map(org => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'medium',
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {currentOrganization.name}
          </Typography>
        )}
        <Typography
          data-testid="organization-label"
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            lineHeight: 1.2,
          }}
        >
          Organisation
        </Typography>
      </Box>
    </Box>
  );
};

export default OrganizationDisplay;
